/**
 * Teri's saved chats — the list behind the header title.
 *
 * Presentational on purpose: it owns the data (its own query) and the row
 * actions, but not its position. The header wraps it in the dropdown; nothing
 * here assumes a width or a dock mode, so the same list works in the 380px
 * rail and in the corner window.
 *
 * Chats are bucketed by when they were last touched rather than listed flat —
 * "the one I had this morning" is how members actually look for a chat, and
 * the server already returns them most-recent-first so the buckets fall out
 * in order without a sort.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { differenceInCalendarDays, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { cn } from '@/lib/utils';
import {
  useConversations,
  useDeleteConversation,
  useRenameConversation,
} from '@/services/assistantConversations';
import type { AssistantConversationSummary } from '@/types/assistant';

interface ConversationHistoryProps {
  /** The chat currently on screen, highlighted in the list. */
  activeId: string | null;
  onSelect: (id: string) => void;
  /** False until the panel opens — nothing is fetched before that. */
  enabled?: boolean;
  /** Chats whose reply is still streaming in the background. */
  answeringIds?: string[];
}

const BUCKETS = ['Today', 'Yesterday', 'Last week', 'Last 30 days', 'Older'] as const;
type Bucket = (typeof BUCKETS)[number];

/** A never-used chat has no `last_message_at`; fall back to when it was made. */
function activityDate(conversation: AssistantConversationSummary): Date | null {
  const raw = conversation.last_message_at ?? conversation.created_at;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function bucketFor(date: Date | null): Bucket {
  if (!date) return 'Older';
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  const days = differenceInCalendarDays(new Date(), date);
  if (days <= 7) return 'Last week';
  if (days <= 30) return 'Last 30 days';
  return 'Older';
}

function groupByBucket(
  conversations: AssistantConversationSummary[]
): [Bucket, AssistantConversationSummary[]][] {
  const groups = new Map<Bucket, AssistantConversationSummary[]>();
  for (const conversation of conversations) {
    const bucket = bucketFor(activityDate(conversation));
    groups.set(bucket, [...(groups.get(bucket) ?? []), conversation]);
  }
  // Fixed bucket order, skipping the empty ones.
  return BUCKETS.filter((b) => groups.has(b)).map((b) => [b, groups.get(b) as AssistantConversationSummary[]]);
}

export function conversationLabel(conversation: AssistantConversationSummary): string {
  return conversation.title?.trim() || 'Untitled chat';
}

function relativeTime(conversation: AssistantConversationSummary): string {
  const date = activityDate(conversation);
  if (!date) return '';
  return formatDistanceToNow(date, { addSuffix: true });
}

interface RowProps {
  conversation: AssistantConversationSummary;
  active: boolean;
  answering: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}

const ConversationRow: React.FC<RowProps> = ({
  conversation,
  active,
  answering,
  onSelect,
  onRename,
  onDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(() => conversationLabel(conversation));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const startEditing = () => {
    setValue(conversationLabel(conversation));
    setEditing(true);
  };

  const commit = () => {
    const next = value.trim();
    setEditing(false);
    if (next && next !== conversationLabel(conversation)) onRename(next);
  };

  if (editing) {
    return (
      <li>
        <input
          ref={inputRef}
          value={value}
          maxLength={120}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            // Swallow both keys: neither may reach the panel behind the list.
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
          }}
          // Cancels rather than commits, so clicking away — or onto delete —
          // never renames a chat the member had stopped renaming.
          onBlur={() => setEditing(false)}
          aria-label={`Rename ${conversationLabel(conversation)}`}
          title="Press Enter to save, Esc to cancel"
          className="w-full px-2.5 py-2 rounded-lg text-[13px] bg-surface-primary text-text-primary border border-primary-500/60 outline-none"
        />
      </li>
    );
  }

  return (
    <li className="group relative">
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'w-full text-left pl-2.5 pr-14 py-2 rounded-lg transition-colors duration-fast',
          active
            ? 'bg-primary-500/10 text-primary-600 dark:text-primary-350'
            : 'text-text-primary hover:bg-text-tertiary/10'
        )}
      >
        <span className="block truncate text-[13px] font-medium leading-tight">
          {conversationLabel(conversation)}
        </span>
        {answering ? (
          // Switching away doesn't stop the reply, so say so where the member
          // just switched from — the row is the only place that can.
          <span className="block mt-0.5 text-[11px] leading-tight truncate text-primary-600 dark:text-primary-350 animate-pulse">
            Teri is still answering…
          </span>
        ) : (
          <span className="block mt-0.5 text-[11px] text-text-tertiary leading-tight truncate">
            {conversation.message_count} message{conversation.message_count === 1 ? '' : 's'}
            {relativeTime(conversation) && ` · ${relativeTime(conversation)}`}
          </span>
        )}
      </button>

      {/* Row actions: quiet until the row is hovered or focused within. */}
      <span className="absolute right-1.5 top-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-fast">
        <button
          type="button"
          onClick={startEditing}
          aria-label={`Rename ${conversationLabel(conversation)}`}
          title="Rename"
          className="w-7 h-7 rounded-full flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-text-tertiary/15 focus-ring"
        >
          <PencilSquareIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${conversationLabel(conversation)}`}
          title="Delete"
          className="w-7 h-7 rounded-full flex items-center justify-center text-text-tertiary hover:text-error-600 hover:bg-error-600/10 focus-ring"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </span>
    </li>
  );
};

const ConversationHistory: React.FC<ConversationHistoryProps> = ({
  activeId,
  onSelect,
  enabled = true,
  answeringIds = [],
}) => {
  const {
    conversations,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useConversations(enabled);
  const rename = useRenameConversation();
  const remove = useDeleteConversation();
  const [pendingDelete, setPendingDelete] = useState<AssistantConversationSummary | null>(
    null
  );

  if (isLoading) {
    return (
      <div className="p-2 space-y-1.5" data-testid="teri-history-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="px-2.5 py-2 space-y-1.5" aria-hidden="true">
            <div className="h-3 rounded bg-text-tertiary/15 animate-pulse" style={{ width: `${70 - i * 8}%` }} />
            <div className="h-2 w-1/3 rounded bg-text-tertiary/10 animate-pulse" />
          </div>
        ))}
        <span className="sr-only">Loading your saved chats…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-4 py-6 text-center" data-testid="teri-history-error">
        <p className="text-[13px] text-text-secondary">Couldn't load your chats.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 text-[13px] font-semibold text-primary-600 dark:text-primary-350 hover:underline focus-ring rounded"
        >
          Try again
        </button>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="px-4 py-8 text-center" data-testid="teri-history-empty">
        <ChatBubbleLeftRightIcon className="w-7 h-7 mx-auto text-text-tertiary/60" />
        <p className="mt-2 text-[13px] text-text-secondary">No saved chats yet</p>
        <p className="mt-0.5 text-[11px] text-text-tertiary">
          Ask Teri something and it will show up here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="p-1.5">
        {groupByBucket(conversations).map(([bucket, rows]) => (
          <section key={bucket} className="mb-1 last:mb-0">
            <h3 className="px-2.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-text-tertiary">
              {bucket}
            </h3>
            <ul className="space-y-0.5">
              {rows.map((conversation) => (
                <ConversationRow
                  key={conversation.id}
                  conversation={conversation}
                  active={conversation.id === activeId}
                  answering={answeringIds.includes(conversation.id)}
                  onSelect={() => onSelect(conversation.id)}
                  onRename={(title) => rename.mutate({ id: conversation.id, title })}
                  onDelete={() => setPendingDelete(conversation)}
                />
              ))}
            </ul>
          </section>
        ))}

        {hasNextPage && (
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="w-full mt-1 px-2.5 py-2 rounded-lg text-[12px] font-medium text-text-secondary hover:bg-text-tertiary/10 disabled:opacity-50 transition-colors duration-fast focus-ring"
          >
            {isFetchingNextPage ? 'Loading…' : 'Show older chats'}
          </button>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete this chat?"
          message={`"${conversationLabel(pendingDelete)}" and everything in it will be gone for good. This can't be undone.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            remove.mutate(pendingDelete.id);
            setPendingDelete(null);
          }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </>
  );
};

export default ConversationHistory;
